import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';

// 2FA無効化状態の型定義
type Disable2FA = {
    loading: boolean;
    data: Record<string, never> | null;
};

// デフォルトの2FA無効化状態
const defaultObj: Disable2FA = {
    loading: false,
    data: null,
};

// フックの戻り値の型定義
type ReturnType = [Disable2FA, () => void, () => void];

type Meta = MFADisableConnection['meta'] | MFADisableConnection['meta'];
type Payload = MFADisableConnection['payload'] | MFADisableConnection['payload'];

/**
 * 2要素認証の無効化を管理するカスタムフック
 * @return {Disable2FA} multiFactorAuth - 2FA無効化の状態（ローディング状態とメッセージ）
 * @return {Function} request - 2FA無効化リクエストを実行する関数
 * @return {Function} resetState - 状態をリセットする関数
 */
export function useDisable2FA(
    successCallback?: (meta: Meta, payload: Payload) => void,
    failureCallback?: (meta: Meta, payload: Payload) => void
): ReturnType {
    const dispatch = useDispatch();
    // eslint-disable-next-line no-undef
    const disable2FAConnection = useAppSelector(
        (state) => state.mfaDisableConnection,
    );
    const [multiFactorAuth, setMultiFactorAuth] =
        useState<Disable2FA>(defaultObj);

    useEffect(() => {
        const { meta, payload } = disable2FAConnection;

        switch (meta.status) {
            case Actions.statusEnum.SUCCESS:
                setMultiFactorAuth((prev) => ({
                    ...prev,
                    loading: meta.fetch,
                    data: payload?.result ?? null,
                }));
                successCallback?.(meta, payload);
                break;
            case Actions.statusEnum.FAILURE:
            case Actions.statusEnum.ERROR:
            case Actions.statusEnum.REQUEST:
                setMultiFactorAuth((prev) => ({
                    ...prev,
                    loading: meta.fetch,
                }));
                failureCallback?.(meta, payload);
                break;
            default:
                // デフォルトケースの処理
                break;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disable2FAConnection]);

    // 2FA無効化リクエストを送信
    const request = useCallback(() => {
        setMultiFactorAuth((prev) => ({
            ...prev,
            loading: true,
        }));

        const disable = Actions.http.connection.authentication.disable;
        dispatch(disable());
    }, [dispatch]);

    // 状態をデフォルト値にリセット
    const resetState = useCallback(() => {
        setMultiFactorAuth({ ...defaultObj });
    }, []);

    return [multiFactorAuth, request, resetState];
}
